import { MouseEvent, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { GridActionsCellItemProps, GridRowParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import fleetImg from 'src/assets/fleet.svg';
import { Button, H1, Loader, P1R, Toolbar } from 'src/components';
import { selectCreateYardSuccess, selectDeleteYardSuccess, selectSaveYardSuccess, selectYards, selectYardsLoading } from 'src/redux/yards/selectors';
import { deleteYard, loadYard, loadYards } from 'src/redux/yards/actions';
import YardsTable from 'src/components/YardsTable';
import StyledActionItem from 'src/components/Table/TableComponents/StyledActionItem';
import { themeColors } from 'src/theme';
import { YardType } from 'src/types';
import DeletePopup from 'src/components/Popup/DeletePopup';
import { DashboardWrapper } from '../wrappers';
import AddCompanyYardDialog from './components/AddCompanyYardDialog';

const FleetCompanyYardsContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const yards = useSelector(selectYards);
  const isYardsLoading = useSelector(selectYardsLoading);
  const isCreateYardSuccess = useSelector(selectCreateYardSuccess);
  const isDeleteYardSuccess = useSelector(selectDeleteYardSuccess);
  const isSaveYardSuccess = useSelector(selectSaveYardSuccess);
  const [editingYard, setEditingYard] = useState<undefined | YardType>(undefined);
  const [yardToDelete, setYardToDelete] = useState<undefined | YardType>(undefined);

  const onEditClick = (event: MouseEvent<HTMLElement>, yard: YardType) => {
    dispatch(loadYard.init(yard));
    event.stopPropagation();
    setEditingYard(yard);
  };

  const onDeleteClick = (event: MouseEvent<HTMLElement>, yard: YardType) => {
    event.stopPropagation();
    setYardToDelete(yard);
  };

  const handleDeleteVehicle = () => {
    if (yardToDelete?.id) {
      dispatch(deleteYard.init(yardToDelete));
    }
  };

  const getActions = (params: GridRowParams<any>): React.ReactElement<GridActionsCellItemProps>[] => {
    const actions: React.ReactElement<GridActionsCellItemProps>[] = [];

    actions.push(
      <StyledActionItem
        key="editCell"
        label="Edit"
        // disabled={editMode}
        onClick={(event: MouseEvent<HTMLElement>) => onEditClick(event, params.row)}
        showInMenu
      />,
    );

    actions.push(
      <StyledActionItem
        key="deleteCell"
        label="Delete"
        onClick={(event: MouseEvent<HTMLElement>) => onDeleteClick(event, params.row)}
        color={themeColors.red}
        showInMenu
      />,
    );
    return actions;
  };

  useEffect(() => {
    dispatch(loadYards.init());
  }, [dispatch]);

  useEffect(() => {
    if (isCreateYardSuccess || isDeleteYardSuccess || isSaveYardSuccess) {
      dispatch(loadYards.init());
    }
  }, [dispatch, isCreateYardSuccess, isDeleteYardSuccess, isSaveYardSuccess]);

  return (
    <DashboardWrapper breadcrumbs={[[t('sidebarItems.companyYards')]]} navigationChildren={<NavigationWidget />}>
      {isYardsLoading ? (
        <Loader />
      ) : yards && yards.length ? (
        <Box component="main" sx={{ height: 'calc(100vh - 100px)', pt: 4 }}>
          <YardsTable yards={yards} getActions={getActions} />
        </Box>
      ) : (
        <Box
          sx={{
            mt: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ ml: 1 }}>
            <img src={fleetImg} alt="Empty list" />
          </Box>
          <H1 sx={{ mb: 3 }}>{t('companyYardsPage.noYards')}</H1>
          <P1R>{t('companyYardsPage.noYardsCreated')}</P1R>
        </Box>
      )}

      {yardToDelete && (
        <DeletePopup
          onClose={() => setYardToDelete(undefined)}
          onDelete={handleDeleteVehicle}
          title={t('companyYardsPage.areYouSure')}
          text={`${t('companyYardsPage.aboutToDeleteYard')} ${yardToDelete.name}`}
        />
      )}
      {editingYard && <AddCompanyYardDialog isEdit onClose={() => setEditingYard(undefined)} yard={editingYard} />}
    </DashboardWrapper>
  );
};

type Props = {
  // editingVehicle?: VehicleType;
  // editMode: boolean;
  // showToolbar: boolean;
};

const NavigationWidget: React.FC<Props> = ({ /* editMode, */ ...props }) => {
  const [showDialog, setShowDialog] = useState(false);
  const { t } = useTranslation();

  return (
    <Box sx={{ gap: 3, display: 'flex' }}>
      <Toolbar />

      <Button
        // disabled={editMode}
        onClick={() => setShowDialog(true)}
        sx={{
          padding: '0 16px',
          textTransform: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {t('companyYardsPage.addYard')}
      </Button>

      {showDialog && <AddCompanyYardDialog onClose={() => setShowDialog(false)} />}
    </Box>
  );
};

export default FleetCompanyYardsContainer;
